import React, {useState} from 'react'
import {Modal} from 'antd'
import {ShoppingCartOutlined} from '@ant-design/icons'
import Card from 'components/shared/Card'
import Button from 'components/shared/Button'
import ProductPreview from './ProductPreview'
import ProductForm from './ProductForm'
import SelectedProductOptions from './SelectedProductOptions'
import './index.scss'

// Merge ordered product (including any changes) on top of the default available product
function mergeProduct({product, orderProduct = {}}) {
  return {...product, ...orderProduct}
}

const ProductDialog = ({
  product,
  bundleProduct,
  orderProduct,
  closeDialog,
  webOrder,
  productAvailability,
  addProduct,
  updateProduct,
}) => {
  const [localProduct, setLocalProduct] = useState(product)

  const onUpdateProduct = (data) => {
    setLocalProduct({...localProduct, ...data})
  }

  const addToCart = () => {
    addProduct(localProduct)
    closeDialog()
  }

  const updateCart = () => {
    updateProduct(localProduct)
    closeDialog()
  }

  const getOkButton = () => {
    if (!ProductForm.productTypeHasFormComponent(product.__typename)) {
      return null
    }

    if (orderProduct) {
      return (
        <Button
          key="update"
          type="action"
          icon={<ShoppingCartOutlined />}
          onClick={updateCart}
        >
          Oppdater
        </Button>
      )
    }

    return (
      <Button
        key="add"
        type="action"
        icon={<ShoppingCartOutlined />}
        onClick={addToCart}
      >
        Legg til
      </Button>
    )
  }

  return (
    <Modal
      visible={true}
      wrapClassName="purchasable-product-dialog"
      width="auto"
      title={product.name}
      onOk={closeDialog}
      onCancel={closeDialog}
      footer={[
        <Button
          secondary
          key="back"
          type="action"
          onClick={closeDialog}
        >
          Avbryt
        </Button>,
        getOkButton(),
      ]}
    >
      <ProductPreview
        product={localProduct}
        productAvailability={productAvailability}
      />

      <div className="product-content">
        <div className="product-descriptions">
          {product.descriptions.map((text, i) => (
            <p key={i}>{text}</p>
          ))}
        </div>

        <ProductForm
          product={localProduct}
          bundleProduct={bundleProduct}
          webOrder={webOrder}
          onChange={onUpdateProduct}
          productAvailability={productAvailability}
        />
      </div>
    </Modal>
  )
}

const PurchasableProductCard = ({
  product,
  addProduct,
  removeProduct,
  updateProduct,
  productInBundle,
  productInOrder,
  productAvailability,
  webOrder,
}) => {
  const bundleProduct = productInBundle(product)
  const orderProduct = productInOrder(product)
  const mergedProduct = mergeProduct({product, orderProduct})

  const [dialogIsOpen, setDialogOpen] = useState(false)

  const openDialog = () => setDialogOpen(true)
  const closeDialog = () => setDialogOpen(false)

  const onClickCartButton = () => {
    if (ProductForm.productTypeHasFormComponent(product.__typename)) {
      openDialog()
    } else {
      addProduct(product)
    }
  }

  const CartButton = () => {
    if (bundleProduct) {
      return null
    }

    if (orderProduct) {
      return (
        <Button
          onClick={() => removeProduct(product)}
        >
          Fjern
        </Button>
      )
    }

    return (
      <Button
        type="action"
        icon={<ShoppingCartOutlined />}
        onClick={onClickCartButton}
      >
        Legg til
      </Button>
    )
  }

  return (
    <Card className="purchasable-product-card">
      <div className="product-header">
        <span>
          {product.name}
          {!!product.isRecommended &&
            <span className="recommended-label">Anbefalt!</span>
          }
        </span>
        {mergedProduct.quantity && <span>{mergedProduct.quantity} STK</span>}
      </div>

      <ProductPreview product={product} />

      <SelectedProductOptions
        product={mergedProduct}
        productAvailability={productAvailability}
      />

      <div className="product-actions">
        <Button
          secondary
          type="action"
          onClick={openDialog}
        >
          Vis/endre
        </Button>
        <CartButton />
      </div>

      {dialogIsOpen && (
        <ProductDialog
          product={mergedProduct}
          bundleProduct={bundleProduct}
          orderProduct={orderProduct}
          closeDialog={closeDialog}
          webOrder={webOrder}
          productAvailability={productAvailability}
          addProduct={addProduct}
          updateProduct={updateProduct}
        />
      )}
    </Card>
  )
}

export default PurchasableProductCard
